'use client';
import React, { useState } from 'react';
import { levels } from '@/lib/mockData';
import type { Language } from '@/lib/i18n';
import { t } from '@/lib/i18n';
import { Plus, Edit2, Trash2, Search, CheckCircle, Lock, Clock, Play } from 'lucide-react';
import { toast } from 'sonner';
import LessonEditorModal from './LessonEditorModal';
import type { Lesson } from '@/lib/mockData';

interface Props { language: Language }

export default function AdminLessons({ language }: Props) {
  const [search, setSearch] = useState('');
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const allLessons = levels.flatMap((level) =>
    level.lessons.map((lesson) => ({ ...lesson, levelTitle: level.title, levelColor: level.color }))
  );

  const filtered = allLessons.filter(
    (l) =>
      l.title.fr.toLowerCase().includes(search.toLowerCase()) ||
      l.title.ru.toLowerCase().includes(search.toLowerCase()) ||
      l.levelTitle.fr.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = (id: string) => {
    setDeletingId(id);
    setTimeout(() => {
      // Backend integration point: DELETE /api/lessons/:id
      toast.success(language === 'fr' ? 'Leçon supprimée' : 'Урок удален');
      setDeletingId(null);
    }, 600);
  };

  const STATUS_CONFIG = {
    completed: { icon: <CheckCircle size={13} />, class: 'bg-green-100 text-green-700' },
    in_progress: { icon: <Clock size={13} />, class: 'bg-amber-100 text-amber-700' },
    available: { icon: <Play size={13} />, class: 'bg-blue-100 text-blue-700' },
    locked: { icon: <Lock size={13} />, class: 'bg-gray-100 text-gray-600' },
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(language, 'common_search')}
            className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          />
        </div>
        <button
          onClick={() => { setEditingLesson(null); setIsModalOpen(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-all active:scale-95"
        >
          <Plus size={15} />
          {t(language, 'admin_add_lesson')}
        </button>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border card-shadow overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-muted/40 border-b border-border">
          {['#', language === 'fr' ? 'Titre' : 'Название', language === 'fr' ? 'Niveau' : 'Уровень', 'XP', 'Statut', language === 'fr' ? 'Actions' : 'Действия'].map((col, i) => (
            <div
              key={`admin-col-${i}`}
              className={`text-xs font-600 text-muted-foreground uppercase tracking-wide ${
                i === 0 ? 'col-span-1' : i === 1 ? 'col-span-4' : i === 2 ? 'col-span-3' : i === 3 ? 'col-span-1' : i === 4 ? 'col-span-2' : 'col-span-1'
              }`}
            >
              {col}
            </div>
          ))}
        </div>

        <div className="divide-y divide-border">
          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <p className="text-sm text-muted-foreground">
                {language === 'fr' ? 'Aucune leçon trouvée' : 'Уроки не найдены'}
              </p>
            </div>
          ) : (
            filtered.map((lesson, i) => {
              const statusConf = STATUS_CONFIG[lesson.status];
              const isDeleting = deletingId === lesson.id;

              return (
                <div
                  key={`admin-lesson-${lesson.id}`}
                  className={`grid grid-cols-12 gap-2 px-5 py-3.5 items-center transition-all duration-300 hover:bg-muted/30 ${
                    isDeleting ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100'
                  }`}
                >
                  <div className="col-span-1 text-xs text-muted-foreground font-tabular">{i + 1}</div>
                  <div className="col-span-4 min-w-0">
                    <p className="text-sm font-600 text-foreground truncate">{lesson.title[language]}</p>
                    <p className="text-xs text-muted-foreground truncate">{lesson.description[language]}</p>
                  </div>
                  <div className="col-span-3">
                    <span
                      className="text-xs font-600 px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: `${lesson.levelColor}20`, color: lesson.levelColor }}
                    >
                      {lesson.levelTitle[language]}
                    </span>
                  </div>
                  <div className="col-span-1 text-sm font-700 text-primary font-tabular">
                    {lesson.xpReward}
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-600 px-2 py-0.5 rounded-full ${statusConf.class}`}>
                      {statusConf.icon}
                      {t(language, `learning_${lesson.status}` as 'learning_locked')}
                    </span>
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    <button
                      onClick={() => { setEditingLesson(lesson); setIsModalOpen(true); }}
                      className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
                      title={t(language, 'admin_edit')}
                    >
                      <Edit2 size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(lesson.id)}
                      className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center text-muted-foreground hover:text-danger hover:bg-danger/10 transition-colors"
                      title={t(language, 'admin_delete')}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <LessonEditorModal
          lesson={editingLesson}
          language={language}
          onClose={() => setIsModalOpen(false)}
          onSave={() => {
            setIsModalOpen(false);
            toast.success(
              editingLesson
                ? language === 'fr' ? 'Leçon modifiée' : 'Урок изменен'
                : language === 'fr' ? 'Leçon créée' : 'Урок создан'
            );
          }}
        />
      )}
    </div>
  );
}